// Chord synonyms: two chord names that voice the SAME pitch-class set. The
// catalogue is full of these because ninth chords drop their root — Cmaj9 is
// spelled 3-5-7-9 = E G B D, which is literally Em7. On the fretboard the grips
// coincide exactly, so the optimiser already plays one when asked for the other.
// (Set-class theory calls these different "root assignments" of one set; Joe
// Pass's "there are only three chords" reductionism is this fact in practice.)

import { CHORD_TYPE_IDS, getChordType, type ChordTypeId } from './chords';
import type { PitchClass } from './notes';

export interface ChordName {
  rootPc: PitchClass;
  typeId: ChordTypeId;
}

/** Canonical key for the pitch-class set a chord voices (root-independent). */
export function pitchClassSetKey(rootPc: PitchClass, typeId: ChordTypeId): string {
  const pcs = getChordType(typeId)
    .intervals.map((iv) => (((rootPc + iv) % 12) + 12) % 12)
    .sort((a, b) => a - b);
  return [...new Set(pcs)].join(',');
}

/** The pitch classes a chord voices, ascending. */
export function chordPitchClasses(rootPc: PitchClass, typeId: ChordTypeId): PitchClass[] {
  return pitchClassSetKey(rootPc, typeId)
    .split(',')
    .map((s) => Number(s));
}

let index: Map<string, ChordName[]> | null = null;

/** Every catalogue name for every pitch-class set, built once (288 entries). */
function synonymIndex(): Map<string, ChordName[]> {
  if (index) return index;
  index = new Map();
  for (let rootPc = 0 as PitchClass; rootPc < 12; rootPc++) {
    for (const typeId of CHORD_TYPE_IDS) {
      const key = pitchClassSetKey(rootPc, typeId);
      const list = index.get(key) ?? [];
      list.push({ rootPc, typeId });
      index.set(key, list);
    }
  }
  return index;
}

/**
 * All other catalogue names for the exact pitch-class set this chord voices.
 * Same-type entries at other roots only appear for symmetric chords (dim7,
 * where four roots share one set); everything else is a genuine cross-type
 * identity like Cmaj9 = Em7.
 */
export function findSynonyms(rootPc: PitchClass, typeId: ChordTypeId): ChordName[] {
  const key = pitchClassSetKey(rootPc, typeId);
  return (synonymIndex().get(key) ?? []).filter(
    (c) => !(c.rootPc === ((rootPc % 12) + 12) % 12 && c.typeId === typeId),
  );
}

/** Group the whole catalogue into identity classes with more than one name. */
export function synonymClasses(): { pcs: PitchClass[]; names: ChordName[] }[] {
  const out: { pcs: PitchClass[]; names: ChordName[] }[] = [];
  synonymIndex().forEach((names, key) => {
    if (names.length > 1) {
      out.push({ pcs: key.split(',').map(Number), names });
    }
  });
  return out.sort((a, b) => b.names.length - a.names.length);
}
